import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { addStationOwner, deleteStationOwner } from "../../../services/owners";
import { getStationsApi } from "../../../services/stations";

const ROLE_OPTIONS = [
  { label: "Manager", value: "manager" },
  { label: "Owner", value: "owner" },
];

const ManageOwnershipDialog = ({ visible, onHide, userEmail, onSuccess }) => {
  const [selectedStation, setSelectedStation] = useState(null);
  const [selectedRole, setSelectedRole] = useState("owner");
  const [loading, setLoading] = useState(false);
  const [stations, setStations] = useState([]);
  const toast = useRef(null);

  useEffect(() => {
    if (!visible || stations.length > 0) return;
    getStationsApi()
      .then((data) => {
        const options = (Array.isArray(data) ? data : []).map((s) => ({
          label: s.name ? `${s.name} (${s.id})` : s.id,
          value: s.id,
        }));
        setStations(options);
      })
      .catch((e) => console.error("Failed to load stations", e));
    // stations.length only ever gates the re-run: once the fetch has populated
    // the list the guard above returns early, so this cannot re-fetch.
  }, [visible, stations.length]);

  const showToast = (severity, summary, detail) => {
    toast.current?.show({ severity, summary, detail, life: 3000 });
  };

  const handleClose = () => {
    setSelectedStation(null);
    setSelectedRole("owner");
    onHide();
  };

  const handleAdd = async () => {
    if (!selectedStation || !userEmail) return;
    setLoading(true);
    try {
      await addStationOwner(selectedStation, userEmail, selectedRole);
      showToast("success", "Added", `${userEmail} added as ${selectedRole} to station ${selectedStation}`);
      await onSuccess();
      handleClose();
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to add";
      showToast("error", "Error", message);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveOwner = async () => {
    if (!selectedStation || !userEmail) return;
    setLoading(true);
    try {
      await deleteStationOwner(selectedStation, userEmail);
      showToast("success", "Owner Removed", `${userEmail} removed from station ${selectedStation}`);
      await onSuccess();
      handleClose();
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to remove owner";
      showToast("error", "Error", message);
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div className="flex gap-2">
      <Button
        label="Remove"
        icon="pi pi-user-minus"
        severity="danger"
        className="flex-1"
        loading={loading}
        disabled={!selectedStation}
        onClick={handleRemoveOwner}
      />
      <Button
        label="Add"
        icon="pi pi-user-plus"
        severity="success"
        className="flex-1"
        loading={loading}
        disabled={!selectedStation}
        onClick={handleAdd}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        header="Manage Ownership"
        visible={visible}
        style={{ width: "400px" }}
        footer={footer}
        onHide={handleClose}
      >
        <div className="flex flex-column gap-3 pt-2">
          <label className="font-medium">Select a station</label>
          <Dropdown
            value={selectedStation}
            options={stations}
            onChange={(e) => setSelectedStation(e.value)}
            placeholder="Select a station"
            filter
            className="w-full"
          />
          <label className="font-medium">Role</label>
          <Dropdown
            value={selectedRole}
            options={ROLE_OPTIONS}
            onChange={(e) => setSelectedRole(e.value)}
            className="w-full"
          />
        </div>
      </Dialog>
    </>
  );
};

export default ManageOwnershipDialog;
